'use client';

import { ColumnDef } from '@tanstack/react-table';
import { ExternalLink } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { DateDisplay } from '@/components/date-display';
import { formatMoney } from "@/lib/money";
import { PaymentStatusBadge } from '@/components/payments/payment-status-badge';
import { PaymentMethodCell, type PaymentMethodInput } from '@/components/payments/payment-method-cell';
import { PaymentResourceBadge } from '@/components/payments/PaymentResourceBadge';
import { useOrgNavigate } from '@/hooks/use-app-navigate';

// Generic payment type that covers both full Payment and subscription payments
type PaymentRow = {
  id: string;
  status: string;
  amount: number;
  currency?: string;
  createdAt: Date;
  chainIndex?: number | null;
  retryIndex?: number | null;
  paymentInstrument?: {
    type?: string;
    card?: {
      brand?: string;
      last4?: string;
    };
  };
  payment?: {
    id: string;
    status: string;
    amount: number;
    currency?: string;
    createdAt: Date;
    subStatus?: string;
    chainIndex?: number | null;
    retryIndex?: number | null;
    paymentInstrument?: {
      type?: string;
      card?: {
        brand?: string;
        last4?: string;
      };
    };
  };
};

/**
 * Lightweight payment table columns for embedded use in subscription detail pages
 * Matches the structure of embedded payments table from orders page
 * Focuses on essential information only
 */
export function useSubscriptionPaymentsTableColumns(): ColumnDef<PaymentRow>[] {
  const { t } = useTranslation('payments');

  const ActionsCell = ({ paymentId }: { paymentId: string }) => {
    const { orgRoutes } = useOrgNavigate();

    return (
      <div className="flex justify-end">
        <Button variant="ghost" size="sm" asChild>
          <Link to={orgRoutes.PAYMENT_DETAILS(paymentId)}>
            <ExternalLink className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    );
  };

  return React.useMemo<ColumnDef<PaymentRow>[]>(
    () => [
      {
        id: 'id',
        accessorKey: 'id',
        header: t('table.columns.paymentId'),
        cell: ({ row }) => {
          const payment = row.original;
          const paymentId = payment.payment?.id || payment.id;
          return <PaymentResourceBadge paymentId={paymentId} />;
        },
        size: 180,
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('table.columns.status'),
        cell: ({ row }) => {
          const payment = row.original;
          const status = (payment.payment?.status || payment.status) as any;
          const subStatus = payment.payment?.subStatus || (payment as any).subStatus;
          return <PaymentStatusBadge status={status} subStatus={subStatus} />;
        },
        size: 120,
      },
      {
        id: 'amount',
        accessorKey: 'amount',
        header: t('table.columns.amount'),
        cell: ({ row }) => {
          const payment = row.original;
          const amount = payment.payment?.amount || payment.amount;
          const currency = payment.payment?.currency || payment.currency || 'USD';

          return (
            <div className="flex flex-col gap-0.5">
              <div className="text-sm font-semibold tabular-nums">{formatMoney(amount, currency)}</div>
              {currency !== 'USD' && (
                <div className="text-muted-foreground text-xs font-medium">{currency}</div>
              )}
            </div>
          );
        },
        size: 120,
      },
      {
        id: 'paymentMethod',
        accessorKey: 'paymentInstrument.card',
        header: t('table.columns.paymentMethod'),
        cell: ({ row }) => {
          // Subscription rows can wrap the actual payment under `.payment` or
          // expose it inline. Pick whichever has the richer instrument data.
          const row0 = row.original as PaymentMethodInput & { payment?: PaymentMethodInput };
          const inputForCell: PaymentMethodInput = row0.payment ?? row0;
          return <PaymentMethodCell payment={inputForCell} />;
        },
        size: 180,
      },
      {
        id: 'retryIndex',
        accessorKey: 'retryIndex',
        header: t('table.columns.retryIndex'),
        cell: ({ row }) => {
          const payment = row.original;
          const retryIndex = payment.payment?.retryIndex ?? payment.retryIndex;
          return <span className="text-muted-foreground text-sm">{retryIndex ?? '-'}</span>;
        },
        size: 80,
      },
      {
        id: 'chainIndex',
        accessorKey: 'chainIndex',
        header: t('table.columns.chainIndex'),
        cell: ({ row }) => {
          const payment = row.original;
          const chainIndex = payment.payment?.chainIndex ?? payment.chainIndex;
          return <span className="text-muted-foreground text-sm">{chainIndex ?? '-'}</span>;
        },
        size: 80,
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: t('table.columns.date'),
        cell: ({ row }) => {
          const payment = row.original;
          const createdAt = payment.payment?.createdAt || payment.createdAt;
          return (
            <DateDisplay
              value={createdAt}
              mode="split"
              className="text-sm tabular-nums"
            />
          );
        },
        size: 130,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const payment = row.original;
          const paymentId = payment.payment?.id || payment.id;

          return <ActionsCell paymentId={paymentId} />;
        },
        size: 60,
      },
    ],
    [t],
  );
}
